import type { ColoanaTabel, RandRaport } from "@/utils/targetareTabel";

export type OperatorFiltru =
  | "contine"
  | "nu_contine"
  | "egal"
  | "diferit"
  | "incepe_cu"
  | "in"
  | "mai_mare"
  | "mai_mare_egal"
  | "mai_mic"
  | "mai_mic_egal"
  | "intre"
  | "este_gol"
  | "nu_este_gol";

export type FiltruRaport = {
  id: string;
  coloana: string;
  operator: OperatorFiltru;
  valoare?: string;
  valoare2?: string;
  valori?: string[];
  activ: boolean;
};

export type CautareRaport = {
  coloana: string;
  text: string;
};

export type SortareRaport = { id: string; desc: boolean };

export type RaportSalvat = {
  nume: string;
  coloane: string[];
  filtre: FiltruRaport[];
  sortare: SortareRaport[];
  randuriPePagina: number;
  cautare: CautareRaport;
};

export type FiltruRapidDef = {
  coloana: string;
  eticheta: string;
  optiuni?: readonly string[];
  etichete?: Record<string, string>;
};

export type DefinitieRaport<R extends RandRaport> = {
  cheie: string;
  coloane: ColoanaTabel<R>[];
  rapoarteImplicite: RaportSalvat[];
  cheieRand: (rand: R) => string;
  filtreRapide?: FiltruRapidDef[];
  numeFisier: string;
  mesajGol?: string;
};

export const CAUTARE_TOATE = "toate";

export const ETICHETE_OPERATOR: Record<OperatorFiltru, string> = {
  contine: "conține",
  nu_contine: "nu conține",
  egal: "este",
  diferit: "nu este",
  incepe_cu: "începe cu",
  in: "este una dintre",
  mai_mare: ">",
  mai_mare_egal: "≥",
  mai_mic: "<",
  mai_mic_egal: "≤",
  intre: "între",
  este_gol: "este gol",
  nu_este_gol: "nu este gol",
};

export function operatoriPentru<R extends RandRaport>(
  coloana: ColoanaTabel<R>
): OperatorFiltru[] {
  switch (coloana.tip) {
    case "numar":
      return [
        "egal",
        "diferit",
        "mai_mare",
        "mai_mare_egal",
        "mai_mic",
        "mai_mic_egal",
        "intre",
        "este_gol",
        "nu_este_gol",
      ];
    case "lista":
      return ["egal", "diferit", "in", "este_gol", "nu_este_gol"];
    case "bool":
      return ["egal"];
    default:
      return [
        "contine",
        "nu_contine",
        "egal",
        "diferit",
        "incepe_cu",
        "in",
        "este_gol",
        "nu_este_gol",
      ];
  }
}

export const RANDURI_PE_PAGINA = [15, 25, 50, 100, 250, 500];

export function coloanaMeta<R extends RandRaport>(
  coloane: ColoanaTabel<R>[],
  id: string
): ColoanaTabel<R> | undefined {
  return coloane.find((c) => c.id === id);
}

const normalizeaza = (v: unknown): string =>
  String(v ?? "")
    .toLowerCase()
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "");

function esteGol(v: unknown): boolean {
  if (v === null || v === undefined) return true;
  if (Array.isArray(v)) return v.length === 0;
  if (typeof v === "string") return v.trim().length === 0;
  return false;
}

function caText(v: unknown): string {
  if (Array.isArray(v)) return v.map(String).join(" | ");
  if (typeof v === "boolean") return v ? "da" : "nu";
  return String(v ?? "");
}

export function potrivesteFiltru<R extends RandRaport>(
  coloane: ColoanaTabel<R>[],
  rand: R,
  f: FiltruRaport
): boolean {
  const meta = coloanaMeta(coloane, f.coloana);
  const v = rand[f.coloana];
  if (f.operator === "este_gol") return esteGol(v);
  if (f.operator === "nu_este_gol") return !esteGol(v);
  if (meta?.tip === "numar") {
    const n =
      typeof v === "number"
        ? v
        : v === null || v === undefined
          ? NaN
          : Number(v);
    const a = Number(f.valoare);
    const b = Number(f.valoare2);
    if (Number.isNaN(n)) return false;
    switch (f.operator) {
      case "egal":
        return n === a;
      case "diferit":
        return n !== a;
      case "mai_mare":
        return n > a;
      case "mai_mare_egal":
        return n >= a;
      case "mai_mic":
        return n < a;
      case "mai_mic_egal":
        return n <= a;
      case "intre":
        return n >= Math.min(a, b) && n <= Math.max(a, b);
      default:
        return true;
    }
  }
  if (meta?.tip === "bool") {
    const dorit = normalizeaza(f.valoare) === "da";
    return Boolean(v) === dorit;
  }
  const text = normalizeaza(caText(v));
  const cautat = normalizeaza(f.valoare);
  const valori = (f.valori ?? []).map(normalizeaza);
  const valoriRand = Array.isArray(v)
    ? v.map(normalizeaza)
    : [normalizeaza(caText(v))];
  switch (f.operator) {
    case "contine":
      return text.includes(cautat);
    case "nu_contine":
      return !text.includes(cautat);
    case "egal":
      return valoriRand.includes(cautat);
    case "diferit":
      return !valoriRand.includes(cautat);
    case "incepe_cu":
      return valoriRand.some((x) => x.startsWith(cautat));
    case "in":
      return valoriRand.some((x) => valori.includes(x));
    default:
      return true;
  }
}

export function potrivesteCautare<R extends RandRaport>(
  coloane: ColoanaTabel<R>[],
  rand: R,
  cautare: CautareRaport,
  coloaneVizibile: string[]
): boolean {
  const text = normalizeaza(cautare.text).trim();
  if (!text) return true;
  const tinta =
    cautare.coloana === CAUTARE_TOATE
      ? coloaneVizibile.filter((id) => {
          const tip = coloanaMeta(coloane, id)?.tip;
          return tip !== "numar" && tip !== "bool";
        })
      : [cautare.coloana];
  return tinta.some((id) => normalizeaza(caText(rand[id])).includes(text));
}

export function aplicaFiltre<R extends RandRaport>(
  coloane: ColoanaTabel<R>[],
  randuri: R[],
  filtre: FiltruRaport[],
  cautare: CautareRaport,
  coloaneVizibile: string[]
): R[] {
  const active = filtre.filter((f) => f.activ);
  return randuri.filter(
    (r) =>
      active.every((f) => potrivesteFiltru(coloane, r, f)) &&
      potrivesteCautare(coloane, r, cautare, coloaneVizibile)
  );
}

export function etichetaValoare<R extends RandRaport>(
  coloane: ColoanaTabel<R>[],
  id: string,
  valoare: string
): string {
  return coloanaMeta(coloane, id)?.etichete?.[valoare] ?? valoare;
}

export function descriereFiltru<R extends RandRaport>(
  coloane: ColoanaTabel<R>[],
  f: FiltruRaport
): string {
  const eticheta = coloanaMeta(coloane, f.coloana)?.eticheta ?? f.coloana;
  const op = ETICHETE_OPERATOR[f.operator];
  if (f.operator === "este_gol" || f.operator === "nu_este_gol")
    return `${eticheta} ${op}`;
  if (f.operator === "in") {
    return `${eticheta} ${op}: ${(f.valori ?? []).map((v) => etichetaValoare(coloane, f.coloana, v)).join(", ")}`;
  }
  if (f.operator === "intre")
    return `${eticheta} ${op} ${f.valoare ?? ""} și ${f.valoare2 ?? ""}`;
  return `${eticheta} ${op} ${etichetaValoare(coloane, f.coloana, f.valoare ?? "")}`;
}

export function idFiltru(): string {
  return `f${Date.now().toString(36)}${Math.random().toString(36).slice(2, 6)}`;
}

export function coloaneImplicite<R extends RandRaport>(
  coloane: ColoanaTabel<R>[]
): string[] {
  return coloane.filter((c) => c.implicit).map((c) => c.id);
}

export function raportImplicit(
  nume: string,
  coloane: string[],
  extra: Partial<Omit<RaportSalvat, "nume" | "coloane">> = {}
): RaportSalvat {
  return {
    nume,
    coloane,
    filtre: [],
    sortare: [],
    randuriPePagina: 50,
    cautare: { coloana: CAUTARE_TOATE, text: "" },
    ...extra,
  };
}

export function citesteRapoarte(cheie: string): RaportSalvat[] {
  try {
    const brut = localStorage.getItem(cheie);
    const lista = brut ? (JSON.parse(brut) as unknown) : [];
    return Array.isArray(lista) ? (lista as RaportSalvat[]) : [];
  } catch {
    return [];
  }
}

export function scrieRapoarte(cheie: string, rapoarte: RaportSalvat[]): void {
  try {
    localStorage.setItem(cheie, JSON.stringify(rapoarte));
  } catch {
    return;
  }
}

export function esteRaportImplicit(
  implicite: RaportSalvat[],
  nume: string
): boolean {
  return implicite.some((r) => r.nume === nume);
}

export function valoriDistincte<R extends RandRaport>(
  randuri: R[],
  id: string,
  limita = 200
): string[] {
  const numar = new Map<string, number>();
  for (const r of randuri) {
    const v = r[id];
    const lista = Array.isArray(v) ? v.map(String) : [caText(v)];
    for (const x of lista) {
      if (!x) continue;
      numar.set(x, (numar.get(x) ?? 0) + 1);
    }
  }
  return [...numar.entries()]
    .sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0], "ro"))
    .slice(0, limita)
    .map(([v]) => v);
}

export function comparaValori(a: unknown, b: unknown): number {
  if (a === null || a === undefined || a === "")
    return b === null || b === undefined || b === "" ? 0 : 1;
  if (b === null || b === undefined || b === "") return -1;
  if (typeof a === "number" && typeof b === "number") return a - b;
  if (typeof a === "boolean" && typeof b === "boolean")
    return Number(a) - Number(b);
  const sa = Array.isArray(a) ? a.join(", ") : String(a);
  const sb = Array.isArray(b) ? b.join(", ") : String(b);
  return sa.localeCompare(sb, "ro", { sensitivity: "base", numeric: true });
}
